import { useEffect, useRef, useState, type JSX } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  useGithubLinkComplete,
  useGithubLinkStatus,
} from '@/hooks/api/useGithub';
import { useToast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/api-errors';
import { decodeBase64Url } from '@/lib/utils';

/**
 * GitHub Account Linking OAuth Callback Handler
 *
 * This page receives the redirect from GitHub after OAuth authorization.
 * It extracts the code and state parameters and completes the account linking.
 *
 * Flow:
 * 1. User clicks "Link GitHub Account" → initiate returns OAuth URL
 * 2. User authorizes on GitHub
 * 3. GitHub redirects here with ?code=...&state=<signed_token>
 * 4. We extract organizationId from state (or URL path)
 * 5. Call POST /api/organizations/:orgId/github/link/callback
 * 6. Navigate to settings with success/error message
 *
 * URL pattern: /integrations/github/link/callback?code=...&state=...
 *
 * The organizationId is embedded in the signed state token and validated
 * by the backend. The state token also includes session binding to ensure
 * the same user who initiated the flow is completing it.
 */
export default function GitHubLinkCallback(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [willRedirect, setWillRedirect] = useState(false);
  const processedRef = useRef(false);

  // We need to decode the state token to get the organizationId
  // The state token contains: { organizationId, sessionUserId, exp, nonce, type }
  // We can decode the base64url payload part to extract the organizationId
  const code = searchParams.get('code');
  const state = searchParams.get('state');

  // Decode organizationId from state token
  const organizationId = (() => {
    if (!state) return null;

    try {
      const parts = state.split('.');
      if (parts.length !== 2) return null;

      const payloadStr = decodeBase64Url(parts[0]);
      const payload = JSON.parse(payloadStr) as {
        organizationId?: string;
        type?: string;
      };

      // Verify it's a link token
      if (payload.type !== 'link') return null;

      return payload.organizationId ?? null;
    } catch {
      return null;
    }
  })();

  // Hooks must be called unconditionally
  const linkCompleteMutation = useGithubLinkComplete(organizationId ?? '');
  const { refetch: refetchLinkStatus } = useGithubLinkStatus(
    organizationId ?? '',
    { enabled: !!organizationId }
  );

  useEffect(() => {
    // Prevent double-processing in React Strict Mode
    if (processedRef.current) return;

    async function handleCallback(): Promise<void> {
      if (!code || !state) {
        setError('Missing required parameters from GitHub callback.');
        return;
      }

      if (!organizationId) {
        setError('Could not determine organization from callback.');
        return;
      }

      // Mark as processed to prevent double-execution
      processedRef.current = true;

      try {
        const result = await linkCompleteMutation.mutateAsync({ code, state });

        // Check if feature is disabled
        if ('enabled' in result && result.enabled === false) {
          setError('GitHub integration is not currently enabled.');
          setWillRedirect(true);
          setTimeout(() => {
            navigate(`/${organizationId}/settings`, { replace: true });
          }, 3000);
          return;
        }

        // Success! Refetch link status (fire-and-forget - don't fail if this errors)
        // The refetch is just to update the UI cache, the actual linking succeeded
        refetchLinkStatus().catch(() => {
          // Ignore refetch errors - the linking was successful
        });

        toast({
          title: 'GitHub account linked',
          description: `Successfully linked to @${result.providerLogin}`,
        });

        // Redirect to settings page
        navigate(`/${organizationId}/settings?github_link=success`, {
          replace: true,
        });
        return;
      } catch (err) {
        // Use the backend's error message directly - it already provides user-friendly messages
        setError(
          getApiErrorMessage(
            err,
            'Failed to link GitHub account. Please try again.'
          )
        );
        setWillRedirect(true);

        // Redirect after showing error
        setTimeout(() => {
          navigate(`/${organizationId}/settings?github_link=error`, {
            replace: true,
          });
        }, 5000);
      }
    }

    void handleCallback();
  }, [
    code,
    state,
    organizationId,
    linkCompleteMutation,
    refetchLinkStatus,
    navigate,
    toast,
  ]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md px-4">
          <h1 className="text-xl font-semibold mb-2 text-destructive">
            GitHub Linking Error
          </h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          {willRedirect ? (
            <p className="text-sm text-muted-foreground">
              Redirecting to settings...
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Please close this page and try again from your organization
              settings.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <h1 className="text-xl font-semibold mb-2">
          Linking GitHub Account...
        </h1>
        <p className="text-muted-foreground">
          Please wait while we complete the linking process.
        </p>
      </div>
    </div>
  );
}
